/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon, FlexRow, FlexCol, Button } from 'sarsaparilla';
import { getValidationErrorStyle, permitCategories } from '../../utils/utilsNew';
import SmartSelector from './SmartSelector';
import * as types from '../../constants';

export default class LaunchPointDetail extends React.Component {
    static propTypes = {
        launchDetail: PropTypes.shape({}),
        handleLaunchDetailChange: PropTypes.func,
        entrances: PropTypes.arrayOf(PropTypes.shape({})),
        exits: PropTypes.arrayOf(PropTypes.shape({})),
        validation: PropTypes.shape({}),
        permitCategory: PropTypes.number,
        entryOnly: PropTypes.bool,
        launchWindows: PropTypes.shape({}),
        useEntryExitPointText: PropTypes.bool,
        customUserInstructions: PropTypes.node,
        customHeaderText: PropTypes.node,
        customEntryPointLabel: PropTypes.node,
        customExitPointLabel: PropTypes.node,
        customStartLocationLabel: PropTypes.node,
        isQuotaModifyAllowed: PropTypes.bool,
        issuance: PropTypes.shape({
            division_id: PropTypes.string,
        }),
        isFieldSales: PropTypes.bool,
        redirectToAvailabilityPage: PropTypes.func,
    };

    static defaultProps = {
        launchDetail: { entryLocationId: '', exitLocationId: '' },
        validation: { displayValidationError: false },
        permitCategory: 0,
        entryOnly: false,
        customUserInstructions: null,
        customHeaderText: null,
        customEntryPointLabel: null,
        customStartLocationLabel: null,
        customExitPointLabel: null,
    };

    constructor(props) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.onChangeDetailsClick = this.onChangeDetailsClick.bind(this);
    }

    onChangeDetailsClick() {
        // external site - simple redirect to detailed availability
        // field sales - pass existing issuance for modification
        this.props.redirectToAvailabilityPage(this.props.issuance);
    }

    handleChange(event) {
        const input = event.target.value;
        const id = event.target.id;
        const launchDetail = { ...this.props.launchDetail };
        let updateLineItems = true;

        switch (id) {
            case 'launch-point':
                launchDetail.entryLocationId = input;
                updateLineItems = false;
                break;
            case 'takeout-point':
                launchDetail.exitLocationId = input;
                updateLineItems = false;
                break;
            case 'launch-time':
                launchDetail.launchTime = input;
                break;
            default:
        }

        this.props.handleLaunchDetailChange(launchDetail, updateLineItems);
    }

    renderLaunchWindow() {
        const { launchWindows } = this.props;

        if (!launchWindows || !launchWindows.display) {
            return null;
        }

        const launchWindowProps = {
            name: 'launch-time',
            id: 'launch-time',
            value: launchWindows.selected || '0',
            handleChange: this.handleChange,
            options: launchWindows.options.map((a) => ({ value: a.id, label: a.name })),
            placeholderOption: { value: '0', label: 'Select a Time' },
        };

        return (
            <div className="per-launch-point-form per-section-group">
                <div className="rec-order-detail-subsection-title">Launch Window</div>
                <p>Select the time you plan on launching your craft.</p>
                <FlexRow>
                    {launchWindowProps.options.length === 1 && (
                        <FlexCol md={6}>
                            <div className="rec-form-item-wrap">
                                <SmartSelector
                                    label="Launch Time"
                                    isRequired
                                    {...launchWindowProps}
                                />
                            </div>
                        </FlexCol>
                    )}
                    {launchWindowProps.options.length > 1 && (
                        <FlexCol md={6}>
                            <div className="rec-form-item-wrap">
                                <SmartSelector
                                    label="Launch Time"
                                    isRequired
                                    {...launchWindowProps}
                                />
                            </div>
                        </FlexCol>
                    )}
                </FlexRow>
            </div>
        );
    }

    renderChangeDetails() {
        if (this.props.isQuotaModifyAllowed) {
            return (
                <div className="rec-form-item-wrap">
                    <Button
                        type="button"
                        appearance="link"
                        className="change-details-link"
                        iconBeforeElement={<Icon iconName="edit" />}
                        onClick={this.onChangeDetailsClick}
                    >
                        Modify this entrance in Detailed Availability
                    </Button>
                </div>
            );
        }
        return null;
    }

    getValidateError(value) {
        return this.props.validation.displayValidationError && !value;
    }

    render() {
        const {
            entrances,
            exits,
            launchDetail,
            permitCategory,
            entryOnly,
            // issuance
        } = this.props;

        if (!entrances || !exits || entrances.length < 1 || exits.length < 1) {
            return null;
        }

        // let entranceOptions = entrances;
        // let exitOptions = exits;

        // // Remove entrances and exits that are not valid for a particular segment
        // // Allowing for divisons with no parent or issuances with no divisions to not be excluded from dropdowns
        // if (issuance.division_id){
        //   entranceOptions = entrances.filter((op) =>
        //     !op.division_id || op.division_id === issuance.division_id);

        //   exitOptions = exits.filter((op) =>
        //     !op.division_id  || op.division_id === issuance.division_id);
        // }

        const entranceValidationHighlight = getValidationErrorStyle(
            this.props.validation,
            types.IS_LAUNCH_LOCATION_EMPTY
        );
        const entranceProps = {
            name: 'launch-point',
            id: 'launch-point',
            value: launchDetail.entryLocationId,
            handleChange: this.handleChange,
            className: entranceValidationHighlight,
            options: entrances.map((a) => ({ value: a.id, label: a.name })),
            placeholderOption: { value: '', label: 'Select an option...' },
            includePlaceholderOption: entrances.length > 1 || false,
        };

        const exitValidationHighlight = getValidationErrorStyle(
            this.props.validation,
            types.IS_TAKEOUT_LOCATION_EMPTY
        );
        const exitProps = {
            name: 'takeout-point',
            id: 'takeout-point',
            value: launchDetail.exitLocationId,
            handleChange: this.handleChange,
            className: exitValidationHighlight,
            options: exits.map((a) => ({ value: a.id, label: a.name })),
            placeholderOption: { value: '', label: 'Select an option...' },
            includePlaceholderOption: exits.length > 1 || false,
        };

        let headerText = 'Entrance and Exit';
        let startLocationText = 'Entrance';
        let endLocationText = 'Exit';
        switch (permitCategory) {
            case permitCategories.get(types.PERMIT_CATEGORY_HIKING):
            case permitCategories.get(types.PERMIT_CATEGORY_WILDERNESS):
                headerText = 'Trailhead Locations';
                startLocationText = 'Starting Trailhead';
                endLocationText = 'Ending Trailhead';
                break;
            case permitCategories.get(types.PERMIT_CATEGORY_RIVER):
                headerText = 'Launch & Take-out Locations';
                startLocationText = 'Launch Location';
                endLocationText = 'Take-out Location';
                break;
            case permitCategories.get(types.PERMIT_CATEGORY_SNOWMOBILE):
                headerText = 'Gate Locations';
                startLocationText = 'Entrance Gate';
                endLocationText = 'Exit Gate';
                break;
            default:
        }

        if (this.props.useEntryExitPointText) {
            headerText = 'Entry & Exit Points';
            startLocationText = 'Entry Point';
            endLocationText = 'Exit Point';
        }
        let userInstructions = (
            <p>
                Select the {startLocationText.toLowerCase()} and{' '}
                {endLocationText.toLowerCase()} you will be using during your trip.
            </p>
        );

        const {
            customUserInstructions,
            customHeaderText,
            customExitPointLabel,
            customStartLocationLabel,
        } = this.props;
        userInstructions = customUserInstructions || userInstructions;
        headerText = customHeaderText || headerText;
        endLocationText = customExitPointLabel || endLocationText;
        startLocationText = customStartLocationLabel || startLocationText;

        return (
            <>
                <div className="per-launch-point-form per-section-group">
                    <div className="rec-order-detail-subsection-title">{headerText}</div>
                    {userInstructions}
                    {this.renderChangeDetails()}
                    <FlexRow>
                        <FlexCol md={6}>
                            <div className="rec-form-item-wrap">
                                <SmartSelector
                                    {...entranceProps}
                                    isRequired
                                    label={startLocationText}
                                    validationError={this.getValidateError(
                                        entranceProps.value
                                    )}
                                />
                            </div>
                        </FlexCol>
                        {!entryOnly && (
                            <FlexCol md={6}>
                                <div className="rec-form-item-wrap">
                                    <SmartSelector
                                        {...exitProps}
                                        isRequired
                                        label={endLocationText}
                                        validationError={this.getValidateError(
                                            exitProps.value
                                        )}
                                    />
                                </div>
                            </FlexCol>
                        )}
                    </FlexRow>
                </div>

                {this.renderLaunchWindow()}
            </>
        );
    }
}
